import {
  BASEMAP_COARSEST_LEVEL,
  BASEMAP_TILE_PIXELS,
  type BasemapPaintStep,
  type BasemapVariable,
  NO_CATEGORY,
  NO_NUMBER,
  placeKey,
  type RasterType,
  type TilePixels,
  type TilePlace,
  type Vector3,
} from "@pax/basemap";
import { createAxis } from "./axis";
import { asColor, asNumber, compile, type Scope, variableUniform } from "./expressions";
import { coveredRaster } from "./feature-drawing";
import type { DrawCamera, Size } from "./map-view";

/**
 * Draws a basemap on the GPU: every screen pixel finds its place on the world, reads each raster
 * there, and runs a paint order over them in linear light. A raster's tiles live as pages of one
 * texture array, as many as are loaded, whatever their level, and a hash table says which page holds
 * which tile. Each pixel reads a raster at the level where a texel is about a screen pixel, or the
 * nearest coarser one loaded, so a world of any size and any mix of detail draws from what's on
 * screen. One set of pages serves every paint order drawn from it, such as an edit view and the
 * finished world, each compiled once and kept.
 *
 * Categories are drawn as rounded pixels, after the Earth budget spike's viewer: each texel keeps its
 * own category and blends with its neighbor's only within a quarter of a texel of their edge (at
 * least a screen pixel, so edges never alias), which rounds the corners where four meet. From further
 * out, while a texel is 4 screen pixels across or less, the blend widens to the whole texel, smooth,
 * narrowing to a quarter by 24 pixels a texel.
 */

export interface RendererWorld {
  orientation: readonly (readonly number[])[];
  /** Each raster, and whether a number raster is drawn smooth or as rounded pixels. */
  rasters: {
    name: string;
    type: RasterType;
    /** The finest level any of its tiles is at. */
    deepest: number;
    step: number;
    enlarge: "nearest" | "bilinear";
    /** A feature set's part, drawn into tiles, which the pixel grid never follows. */
    feature?: boolean;
  }[];
  expressions: Record<string, unknown>;
}

/** A paint order to draw the world with, and the variables it reads. */
export interface Paint {
  paintOrder: BasemapPaintStep[];
  variables: Record<string, BasemapVariable>;
}

interface DrawOptions {
  camera: DrawCamera;
  size: Size;
  paint: Paint;
  dark: boolean;
  /** Variables' values, by name. */
  values: Record<string, number | boolean>;
  /** Lines between pixels, once they're big enough to see. */
  grid: boolean;
  /** Layers left out of the paint order for now, by raster name. */
  hidden: ReadonlySet<string>;
  /**
   * The brush under the pointer: the pixels of a raster `size` texels across a face whose middles
   * are within `radians` of `direction`, which are the pixels a dab paints, outlined.
   */
  brush: { direction: Vector3; radians: number; size: number } | null;
  /** How much of the world's axis shows through the poles, 0 to 1, on the globe. */
  axis?: number;
  /** Pictures drawn over the world, the first on top, up to `MOST_OVERLAYS` whose images are set. */
  overlays?: DrawnOverlay[];
}

/** A picture pinned over the world, such as a reference photo an author traces. */
interface DrawnOverlay {
  id: string;
  projection: "plateCarree" | "webMercator";
  /** Its edges, in degrees. An east less than its west crosses the antimeridian. */
  bounds: { west: number; south: number; east: number; north: number };
  opacity: number;
}

/** The most overlays drawn at once. */
const MOST_OVERLAYS = 4;

export interface Renderer {
  /**
   * How many device pixels one map pixel may cover before a finer level is read: a reader's
   * `texelTarget`. The streamer loads to the same.
   */
  readonly texelTarget: number;
  /** Takes a world's rasters, keeping the tiles of those whose type is unchanged. */
  setWorld: (world: RendererWorld) => void;
  /**
   * Puts a tile's pixels on a page, the least recently kept one if every page is taken, or just a
   * rectangle of them on the tile's page. False when it isn't a raster of this world.
   */
  setTile: (
    raster: string,
    place: TilePlace,
    pixels: TilePixels,
    rect?: { x: number; y: number; width: number; height: number },
  ) => boolean;
  hasTile: (raster: string, place: TilePlace) => boolean;
  /** Gives back the pages of a raster's tiles at or under a place, such as one made coarser. */
  dropTiles: (raster: string, under: TilePlace) => void;
  /** Marks tiles in use, so the pages given away first are others. */
  keep: (raster: string, places: TilePlace[]) => void;
  /** Gives an overlay its picture, or lets it go. */
  setOverlayImage: (id: string, image: ImageBitmap | null) => void;
  /** Draws to the canvas. Says why the paint order's shader didn't build, if it didn't. */
  draw: (options: DrawOptions) => string | null;
  /**
   * Draws into another canvas, by way of a corner of this one, so it shares these textures. Draw
   * to this canvas after it in the same frame. False when this canvas isn't yet big enough to
   * borrow, as before its first draw, or while the paint order's shader compiles.
   */
  drawInto: (target: HTMLCanvasElement, options: DrawOptions) => boolean;
  dispose: () => void;
}

/** The page table: slots, in rows this wide, and how far a lookup probes past its hashed slot. */
const TABLE_SLOTS = 8192;
const TABLE_WIDTH = 128;
const TABLE_PROBES = 16;

/** The slot a tile hashes to, as the shader's `pageOf` works it out. */
const slotOf = (head: number, x: number, y: number) =>
  (Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(head, 83492791)) & (TABLE_SLOTS - 1);

const VERTEX = `#version 300 es
in vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }`;

const COMMON = `
const float PI = 3.14159265358979;
uniform vec2 u_resolution;
uniform int u_view;
uniform vec2 u_flatCenter;
uniform float u_scale;
uniform vec3 u_target;
uniform vec3 u_east;
uniform vec3 u_north;
uniform float u_altitude;
uniform float u_curvature;
uniform float u_focal;
uniform mat3 u_toCube;
uniform bool u_dark;
// How much ground a screen pixel covers here, worked out from the pixel's neighbors.
float g_metersPerPixel = 0.0;
uniform bool u_grid;
uniform float u_gridLevel;
uniform vec3 u_brush;
uniform float u_brushRadians;
uniform float u_brushSize;
// Device pixels one map pixel may cover before a finer level is read.
uniform float u_texelTarget;
// Pictures over the world: each one's edges in radians (west, south, east, north, east past west),
// whether it's Web Mercator rather than plate carrée, and its opacity.
uniform sampler2D u_overlay0;
uniform sampler2D u_overlay1;
uniform sampler2D u_overlay2;
uniform sampler2D u_overlay3;
uniform int u_overlays;
uniform vec4 u_overlayBounds[4];
uniform float u_overlayMercator[4];
uniform float u_overlayOpacity[4];
uniform vec3 u_background;
out vec4 outColor;

vec4 toLinear(vec4 c) {
  vec3 low = c.rgb / 12.92;
  vec3 high = pow((c.rgb + 0.055) / 1.055, vec3(2.4));
  return vec4(mix(high, low, vec3(lessThanEqual(c.rgb, vec3(0.04045)))), c.a);
}
vec4 toSrgb(vec4 c) {
  vec3 low = c.rgb * 12.92;
  vec3 high = 1.055 * pow(max(c.rgb, 0.0), vec3(1.0 / 2.4)) - 0.055;
  return vec4(mix(high, low, vec3(lessThanEqual(c.rgb, vec3(0.0031308)))), c.a);
}
float linearStep(float a, float b, float x) { return clamp((x - a) / (b - a), 0.0, 1.0); }
float easedStep(float a, float b, float x) { float t = linearStep(a, b, x); return t * t * (3.0 - 2.0 * t); }

// A cube-frame direction to its face position (s, t) and face.
vec3 faceSt(vec3 d) {
  vec3 a = abs(d);
  float face;
  vec2 uv;
  if (a.x >= a.y && a.x >= a.z) {
    face = d.x > 0.0 ? 0.0 : 3.0;
    uv = d.x > 0.0 ? d.yz / d.x : d.zy / d.x;
  } else if (a.y >= a.z) {
    face = d.y > 0.0 ? 1.0 : 4.0;
    uv = d.y > 0.0 ? vec2(-d.x, d.z) / d.y : vec2(d.z, -d.x) / d.y;
  } else {
    face = d.z > 0.0 ? 2.0 : 5.0;
    uv = d.z > 0.0 ? vec2(-d.x, -d.y) / d.z : vec2(-d.y, -d.x) / d.z;
  }
  return vec3((atan(uv) / (PI / 4.0) + 1.0) / 2.0, face);
}
// A face position back to a cube-frame direction.
vec3 faceDirection(float face, vec2 st) {
  vec2 uv = tan(PI / 4.0 * (2.0 * st - 1.0));
  if (face < 0.5) return vec3(1.0, uv.x, uv.y);
  if (face < 1.5) return vec3(-uv.x, 1.0, uv.y);
  if (face < 2.5) return vec3(-uv.x, -uv.y, 1.0);
  if (face < 3.5) return vec3(-1.0, -uv.y, -uv.x);
  if (face < 4.5) return vec3(uv.y, -1.0, -uv.x);
  return vec3(uv.y, uv.x, -1.0);
}
// Rounded pixels: the weights of the 2 × 2 texels around texel position p, each texel's own value
// held flat across its middle and blended with its neighbor's only within a quarter of a texel of
// their edge (at least a screen pixel, \`width\` texels), widening to the whole texel, smooth, while
// a texel is 4 screen pixels across or less, and narrowing to a quarter by 24.
vec4 roundedWeights(vec2 p, vec2 width, out ivec2 corner) {
  vec2 q = p - 0.5;
  corner = ivec2(floor(q));
  float pixels = 1.0 / max(max(width.x, width.y), 1e-6);
  float reach = mix(0.5, 0.25, smoothstep(4.0, 24.0, pixels));
  vec2 r = clamp(max(vec2(reach), width), 0.0, 0.5);
  vec2 g = smoothstep(0.5 - r, 0.5 + r, fract(q));
  return vec4((1.0 - g.x) * (1.0 - g.y), g.x * (1.0 - g.y), (1.0 - g.x) * g.y, g.x * g.y);
}
// Whether the brush paints a texel of the brushed raster: its middle is within the brush. A texel
// past a face's side reads as its neighbor on the next face.
bool brushed(float face, vec2 texel) {
  vec2 st = vec2((texel.x + 0.5) / u_brushSize, 1.0 - (texel.y + 0.5) / u_brushSize);
  return dot(normalize(faceDirection(face, st)), u_brush) >= cos(u_brushRadians);
}
const ivec2 CORNERS[4] = ivec2[4](ivec2(0, 0), ivec2(1, 0), ivec2(0, 1), ivec2(1, 1));
// Which page of its raster's texture holds a tile, or -1 where it isn't loaded: a hash table the
// renderer keeps as the tiles come and go, probed the same way.
uniform highp usampler2D u_pages;
int pageOf(int raster, int level, int face, ivec2 tile) {
  if (tile.x < 0 || tile.y < 0) return -1;
  uint head = uint(raster * 512 + level * 8 + face);
  uint x = uint(tile.x);
  uint y = uint(tile.y);
  uint slot = (x * 73856093u ^ y * 19349663u ^ head * 83492791u) & ${TABLE_SLOTS - 1}u;
  for (int probe = 0; probe < ${TABLE_PROBES}; probe++) {
    int at = int((slot + uint(probe)) & ${TABLE_SLOTS - 1}u);
    uvec4 entry = texelFetch(u_pages, ivec2(at % ${TABLE_WIDTH}, at / ${TABLE_WIDTH}), 0);
    if (entry.w == 0u) return -1;
    if (entry.x == head && entry.y == x && entry.z == y) return int(entry.w) - 1;
  }
  return -1;
}
// The level a raster is read at here: the coarsest whose texels cover no more than u_texelTarget
// device pixels, no finer than it goes, and no finer than what's loaded, so a place still loading
// shows its coarser self.
int levelHere(int raster, vec3 f, float deepest) {
  vec2 st = vec2(f.x, 1.0 - f.y);
  vec2 w = fwidth(st * ${BASEMAP_TILE_PIXELS}.0);
  float wanted = ceil(-log2(max(max(w.x, w.y), 1e-9) * u_texelTarget) - 1e-3);
  int level = int(clamp(${BASEMAP_COARSEST_LEVEL}.0 + wanted, ${BASEMAP_COARSEST_LEVEL}.0, deepest));
  for (; level >= ${BASEMAP_COARSEST_LEVEL}; level--) {
    float across = exp2(float(level - ${BASEMAP_COARSEST_LEVEL}));
    ivec2 tile = ivec2(clamp(floor(st * across), vec2(0.0), vec2(across - 1.0)));
    if (pageOf(raster, level, int(f.z), tile) >= 0) return level;
  }
  // Nothing coarser is loaded here, so the nearest finer tile is read instead.
  for (level = int(deepest); level > ${BASEMAP_COARSEST_LEVEL}; level--) {
    float across = exp2(float(level - ${BASEMAP_COARSEST_LEVEL}));
    ivec2 tile = ivec2(clamp(floor(st * across), vec2(0.0), vec2(across - 1.0)));
    if (pageOf(raster, level, int(f.z), tile) >= 0) return level;
  }
  return ${BASEMAP_COARSEST_LEVEL};
}
float latitudeOf(vec3 d) { return degrees(asin(clamp(normalize(transpose(u_toCube) * d).z, -1.0, 1.0))); }
`;

function readers(raster: RendererWorld["rasters"][number], index: number): string {
  return pages(raster.type, index) + ownReaders(raster, index);
}

/**
 * How a raster reads a texel at the level it's read at here, `g_level`, from whichever loaded tile
 * holds it: its own, or the nearest coarser one while it loads.
 */
function pages(type: RasterType, index: number): string {
  const [sampler, value, none] =
    type === "color"
      ? ["sampler2DArray", "vec4", "vec4(0.0)"]
      : type === "category"
        ? ["usampler2DArray", "uint", `${NO_CATEGORY}u`]
        : ["isampler2DArray", "int", `${NO_NUMBER}`];
  const read = type === "color" ? "" : ".r";
  return `
uniform highp ${sampler} u_raster${index};
uniform float u_deepest${index};
int g_level${index} = ${BASEMAP_COARSEST_LEVEL};
float g_size${index} = ${BASEMAP_TILE_PIXELS}.0;
${value} fetch${index}(ivec2 p, int face) {
  ivec2 q = clamp(p, ivec2(0), ivec2(int(g_size${index}) - 1));
  for (int level = g_level${index}; level >= ${BASEMAP_COARSEST_LEVEL}; level--) {
    int page = pageOf(${index}, level, face, q / ${BASEMAP_TILE_PIXELS});
    if (page >= 0) return texelFetch(u_raster${index}, ivec3(q % ${BASEMAP_TILE_PIXELS}, page), 0)${read};
    q /= 2;
  }
  return ${none};
}
void readLevel${index}(vec3 d) {
  vec3 f = faceSt(d);
  g_level${index} = levelHere(${index}, f, u_deepest${index});
  g_size${index} = ${BASEMAP_TILE_PIXELS}.0 * exp2(float(g_level${index} - ${BASEMAP_COARSEST_LEVEL}));
}`;
}

function ownReaders(raster: RendererWorld["rasters"][number], index: number): string {
  const { type, step } = raster;
  const size = `g_size${index}`;
  if (type === "color") {
    return `
vec4 colorOf${index}(vec3 d) {
  vec3 f = faceSt(d);
  vec2 p = vec2(f.x, 1.0 - f.y) * ${size} - 0.5;
  ivec2 base = ivec2(floor(p));
  vec2 w = fract(p);
  int face = int(f.z);
  return mix(
    mix(fetch${index}(base, face), fetch${index}(base + ivec2(1, 0), face), w.x),
    mix(fetch${index}(base + ivec2(0, 1), face), fetch${index}(base + ivec2(1, 1), face), w.x),
    w.y);
}
bool hasData${index}(vec3 d) { return colorOf${index}(d).a > 0.0; }`;
  }
  if (type === "category") {
    return `
// The category the paint order is reading: this pixel's, or its runner-up's while an edge is drawn.
float g_category${index} = 255.0;
float valueOf${index}(vec3 d) { return g_category${index}; }
bool hasData${index}(vec3 d) { return g_category${index} != 255.0; }
float categoryAt${index}(ivec2 p, int face) {
  return float(fetch${index}(p, face));
}
// The category drawn here, the runner-up it's edged against, and how much the first shows: each
// category scores the rounded weight of its texels among the 2 × 2 around the point.
vec3 rounded${index}(vec3 d) {
  vec3 f = faceSt(d);
  vec2 p = vec2(f.x, 1.0 - f.y) * ${size};
  ivec2 corner;
  vec4 w = roundedWeights(p, fwidth(p), corner);
  int face = int(f.z);
  vec4 id = vec4(
    categoryAt${index}(corner, face),
    categoryAt${index}(corner + ivec2(1, 0), face),
    categoryAt${index}(corner + ivec2(0, 1), face),
    categoryAt${index}(corner + ivec2(1, 1), face));
  vec4 score = vec4(0.0);
  for (int i = 0; i < 4; i++) {
    for (int j = 0; j < 4; j++) score[i] += id[i] == id[j] ? w[j] : 0.0;
  }
  int best = 0;
  for (int i = 1; i < 4; i++) if (score[i] > score[best]) best = i;
  int second = best;
  for (int i = 0; i < 4; i++) {
    if (id[i] != id[best] && (second == best || score[i] > score[second])) second = i;
  }
  float lead = score[best] - score[second];
  float shown = second == best ? 1.0 : clamp(0.5 + lead / max(fwidth(lead), 1e-6), 0.0, 1.0);
  return vec3(id[best], id[second], shown);
}`;
  }
  if (raster.enlarge === "nearest") return roundedNumbers(index, step);
  return `
const int NONE${index} = -2147483648;
int countAt${index}(ivec2 p, int face) { return fetch${index}(p, face); }
float valueOf${index}(vec3 d) {
  vec3 f = faceSt(d);
  vec2 at = vec2(f.x, 1.0 - f.y) * ${size} - 0.5;
  ivec2 base = ivec2(floor(at));
  vec2 w = at - floor(at);
  int face = int(f.z);
  int a = countAt${index}(base, face), b = countAt${index}(base + ivec2(1, 0), face);
  int c = countAt${index}(base + ivec2(0, 1), face), e = countAt${index}(base + ivec2(1, 1), face);
  if (a == NONE${index} || b == NONE${index} || c == NONE${index} || e == NONE${index}) {
    int nearest = countAt${index}(ivec2(round(at)), face);
    return nearest == NONE${index} ? 0.0 : float(nearest) * ${step.toFixed(6)};
  }
  return mix(mix(float(a), float(b), w.x), mix(float(c), float(e), w.x), w.y) * ${step.toFixed(6)};
}
bool hasData${index}(vec3 d) {
  vec3 f = faceSt(d);
  return countAt${index}(ivec2(vec2(f.x, 1.0 - f.y) * ${size}), int(f.z)) != NONE${index};
}
// Slope and aspect from the heights a pixel to each side, in true meters on the world.
vec2 gradient${index}(vec3 d) {
  vec3 f = faceSt(d);
  float texel = 1.0 / ${size};
  vec3 east = normalize(faceDirection(f.z, f.xy + vec2(texel, 0.0)));
  vec3 west = normalize(faceDirection(f.z, f.xy - vec2(texel, 0.0)));
  vec3 up = normalize(faceDirection(f.z, f.xy + vec2(0.0, texel)));
  vec3 down = normalize(faceDirection(f.z, f.xy - vec2(0.0, texel)));
  float radius = 6371008.8;
  float dx = (valueOf${index}(east) - valueOf${index}(west)) / (acos(clamp(dot(east, west), -1.0, 1.0)) * radius);
  float dy = (valueOf${index}(up) - valueOf${index}(down)) / (acos(clamp(dot(up, down), -1.0, 1.0)) * radius);
  vec3 n = normalize(d);
  vec3 alongS = normalize(east - west);
  vec3 alongT = normalize(up - down);
  vec3 g = dx * alongS + dy * alongT;
  vec3 pole = u_toCube * vec3(0.0, 0.0, 1.0);
  vec3 north = normalize(pole - n * dot(pole, n));
  vec3 eastward = cross(north, n);
  return vec2(dot(g, eastward), dot(g, north));
}
float slopeOf${index}(vec3 d) { return length(gradient${index}(d)); }
float aspectOf${index}(vec3 d) {
  vec2 g = gradient${index}(d);
  return mod(degrees(atan(-g.x, -g.y)) + 360.0, 360.0);
}`;
}

/**
 * A number raster drawn as rounded pixels: each texel's own value and its own slope, from the texels
 * on either side of it, blended with its neighbors' only near their edges. How wide a texel is on
 * screen is measured once at the top of the shader, `g_width`, where derivatives are defined.
 */
function roundedNumbers(index: number, step: number): string {
  const size = `g_size${index}`;
  const scale = step.toFixed(6);
  return `
const int NONE${index} = -2147483648;
vec2 g_width${index} = vec2(1.0);
// The 2 × 2 texels around this pixel and their rounded weights, set at the top of the shader, and
// which of them a step is being worked out for, or -1 for the blend of all four.
ivec2 g_corner${index};
int g_face${index};
vec4 g_weights${index};
int g_texel${index} = -1;
int countAt${index}(ivec2 p, int face) { return fetch${index}(p, face); }
// A texel's value, or \`otherwise\` where it holds none.
float heightAt${index}(ivec2 p, int face, float otherwise) {
  int count = countAt${index}(p, face);
  return count == NONE${index} ? otherwise : float(count) * ${scale};
}
void roundTexels${index}(vec3 d) {
  vec3 f = faceSt(d);
  g_face${index} = int(f.z);
  g_weights${index} = roundedWeights(vec2(f.x, 1.0 - f.y) * ${size}, g_width${index}, g_corner${index});
}
float valueOf${index}(vec3 d) {
  if (g_texel${index} >= 0) {
    return heightAt${index}(g_corner${index} + CORNERS[g_texel${index}], g_face${index}, 0.0);
  }
  vec3 f = faceSt(d);
  vec2 p = vec2(f.x, 1.0 - f.y) * ${size};
  int face = int(f.z);
  float own = heightAt${index}(ivec2(p), face, 0.0);
  ivec2 corner;
  vec4 w = roundedWeights(p, g_width${index}, corner);
  float value = 0.0;
  for (int i = 0; i < 4; i++) value += w[i] * heightAt${index}(corner + CORNERS[i], face, own);
  return value;
}
bool hasData${index}(vec3 d) {
  vec3 f = faceSt(d);
  return countAt${index}(ivec2(vec2(f.x, 1.0 - f.y) * ${size}), int(f.z)) != NONE${index};
}
// Each texel's slope from the texels on either side of it, rounded, as eastward and northward rises.
vec2 gradient${index}(vec3 d) {
  vec3 f = faceSt(d);
  vec2 p = vec2(f.x, 1.0 - f.y) * ${size};
  int face = int(f.z);
  ivec2 corner;
  vec4 w = roundedWeights(p, g_width${index}, corner);
  vec2 rise = vec2(0.0);
  for (int i = 0; i < 4; i++) {
    // A step worked out for one texel reads that texel's own slope alone.
    if (g_texel${index} >= 0) {
      w = vec4(0.0);
      w[g_texel${index}] = 1.0;
      corner = g_corner${index};
      face = g_face${index};
    }
    ivec2 c = corner + CORNERS[i];
    float here = heightAt${index}(c, face, 0.0);
    // Texel rows count down the face while t counts up it.
    rise += w[i] * vec2(
      heightAt${index}(c + ivec2(1, 0), face, here) - heightAt${index}(c - ivec2(1, 0), face, here),
      heightAt${index}(c - ivec2(0, 1), face, here) - heightAt${index}(c + ivec2(0, 1), face, here)) / 2.0;
  }
  float texel = 1.0 / ${size};
  vec3 east = normalize(faceDirection(f.z, f.xy + vec2(texel, 0.0)));
  vec3 west = normalize(faceDirection(f.z, f.xy - vec2(texel, 0.0)));
  vec3 up = normalize(faceDirection(f.z, f.xy + vec2(0.0, texel)));
  vec3 down = normalize(faceDirection(f.z, f.xy - vec2(0.0, texel)));
  float radius = 6371008.8;
  float dx = rise.x / (acos(clamp(dot(east, west), -1.0, 1.0)) / 2.0 * radius);
  float dy = rise.y / (acos(clamp(dot(up, down), -1.0, 1.0)) / 2.0 * radius);
  vec3 n = normalize(d);
  vec3 g = dx * normalize(east - west) + dy * normalize(up - down);
  vec3 pole = u_toCube * vec3(0.0, 0.0, 1.0);
  vec3 north = normalize(pole - n * dot(pole, n));
  vec3 eastward = cross(north, n);
  return vec2(dot(g, eastward), dot(g, north));
}
float slopeOf${index}(vec3 d) { return length(gradient${index}(d)); }
float aspectOf${index}(vec3 d) {
  vec2 g = gradient${index}(d);
  return mod(degrees(atan(-g.x, -g.y)) + 360.0, 360.0);
}`;
}

function fragmentFor(world: RendererWorld, paint: Paint, hidden: ReadonlySet<string>): string {
  const scope: Scope = {
    rasters: Object.fromEntries(
      world.rasters.map((raster, index) => [raster.name, { index, type: raster.type }]),
    ),
    variables: paint.variables,
    expressions: world.expressions,
  };
  const variables = Object.entries(paint.variables)
    .map(
      ([name, variable]) =>
        `uniform ${variable.type === "boolean" ? "bool" : "float"} ${variableUniform(name)};`,
    )
    .join("\n");
  const steps = paint.paintOrder
    .map(step => {
      const featureSet = typeof step.covers === "string" ? step.covers : step.covers.featureSet;
      if (hidden.has(featureSet)) return "";
      const raster = scope.rasters[coveredRaster(step.covers, scope.rasters)];
      if (!raster) return "";
      const own =
        raster.type === "color"
          ? `colorOf${raster.index}(direction)`
          : "vec4(0.85, 0.33, 0.77, 1.0)";
      const color =
        step.appearance.color === undefined ? own : asColor(compile(step.appearance.color, scope));
      const opacity =
        step.appearance.opacity === undefined
          ? "1.0"
          : asNumber(compile(step.appearance.opacity, scope));
      const coverage =
        raster.type === "color"
          ? `colorOf${raster.index}(direction).a`
          : `(hasData${raster.index}(direction) ? 1.0 : 0.0)`;
      const blend =
        step.blend === "multiply"
          ? "paint *= mix(vec3(1.0), painted.rgb, amount);"
          : "paint = mix(paint, painted.rgb, amount);";
      // A category's step is worked out for the runner-up too, and the two blended at the edge.
      // A rounded number raster's is worked out for each of the 4 texels around the pixel, which
      // group by how much they paint; the leading group is edged against the runner-up the same
      // way, so a coastline drawn from heights falls on the same pixel edges as the land cover's,
      // and within a group the texels' colors blend.
      const rounded =
        raster.type !== "color" &&
        raster.type !== "category" &&
        world.rasters[raster.index]?.enlarge === "nearest";
      if (rounded) {
        const i = raster.index;
        return `{
    vec4 colors[4];
    float amounts[4];
    for (int corner = 0; corner < 4; corner++) {
      g_texel${i} = corner;
      vec4 painted = toLinear(${color});
      amounts[corner] = clamp(${opacity}, 0.0, 1.0) * ${coverage} * painted.a;
      colors[corner] = painted;
    }
    g_texel${i} = -1;
    vec4 score = vec4(0.0);
    for (int a = 0; a < 4; a++) {
      for (int b = 0; b < 4; b++) score[a] += abs(amounts[a] - amounts[b]) < 1e-4 ? g_weights${i}[b] : 0.0;
    }
    int best = 0;
    for (int a = 1; a < 4; a++) if (score[a] > score[best]) best = a;
    int second = best;
    for (int a = 0; a < 4; a++) {
      if (abs(amounts[a] - amounts[best]) >= 1e-4 && (second == best || score[a] > score[second])) second = a;
    }
    vec4 bestColor = vec4(0.0), secondColor = vec4(0.0);
    for (int a = 0; a < 4; a++) {
      float w = g_weights${i}[a];
      if (abs(amounts[a] - amounts[best]) < 1e-4) bestColor += w * colors[a];
      else if (abs(amounts[a] - amounts[second]) < 1e-4) secondColor += w * colors[a];
    }
    bestColor /= max(score[best], 1e-6);
    secondColor = second == best ? bestColor : secondColor / max(score[second], 1e-6);
    float lead = score[best] - score[second];
    float shown = second == best ? 1.0 : clamp(0.5 + lead / max(fwidth(lead), 1e-6), 0.0, 1.0);
    vec4 painted = mix(secondColor, bestColor, shown);
    float amount = mix(amounts[second], amounts[best], shown);
    if (amount > 0.0) {
      ${blend}
    }
  }`;
      }
      const read =
        raster.type === "category"
          ? `g_category${raster.index} = rounded${raster.index}.y;
    float coveredBelow = ${coverage};
    vec4 paintedBelow = toLinear(${color});
    float opacityBelow = ${opacity};
    g_category${raster.index} = rounded${raster.index}.x;
    float shown = rounded${raster.index}.z;
    float covered = mix(coveredBelow, ${coverage}, shown);
    vec4 painted = mix(paintedBelow, toLinear(${color}), shown);
    float opacity = mix(opacityBelow, ${opacity}, shown);`
          : `float covered = ${coverage};
    vec4 painted = toLinear(${color});
    float opacity = ${opacity};`;
      return `{
    ${read}
    if (covered > 0.0) {
      float amount = clamp(opacity, 0.0, 1.0) * covered * painted.a;
      ${blend}
    }
  }`;
    })
    .join("\n  ");

  const levels = world.rasters.map((_, index) => `readLevel${index}(direction);`).join("\n  ");
  const categories = world.rasters
    .map((raster, index) =>
      raster.type === "category"
        ? `vec3 rounded${index} = rounded${index}(direction);
  g_category${index} = rounded${index}.x;`
        : raster.type !== "color" && raster.enlarge === "nearest"
          ? `g_width${index} = fwidth(faceSt(direction).xy * g_size${index});
  roundTexels${index}(direction);`
          : "",
    )
    .join("\n  ");

  return `#version 300 es
precision highp float;
precision highp int;
${COMMON}
${variables}
${world.rasters.map(readers).join("\n")}

void main() {
  vec3 world;
  float shade = 1.0;
  float edge = 1.0;
  if (u_view == 0) {
    vec2 lonLat = u_flatCenter + (gl_FragCoord.xy - u_resolution / 2.0) / u_scale;
    if (abs(lonLat.y) > PI / 2.0) { outColor = vec4(0.0); return; }
    world = vec3(cos(lonLat.y) * cos(lonLat.x), cos(lonLat.y) * sin(lonLat.x), sin(lonLat.y));
  } else {
    // The ray through this pixel against the drawn sphere tangent at the target, as globe.ts's pick.
    vec2 at = gl_FragCoord.xy - u_resolution / 2.0;
    vec3 d = normalize(-u_target + (at.x * u_east + at.y * u_north) / u_focal);
    float h = u_altitude, k = u_curvature;
    float b = dot(d, u_target) * (1.0 + k * h);
    float c = h * (k * h + 2.0);
    float disc = b * b - k * c;
    edge = k > 0.0 ? clamp(disc / max(fwidth(disc), 1e-30) + 0.5, 0.0, 1.0) : 1.0;
    if (b >= 0.0 || edge <= 0.0) { outColor = vec4(0.0); return; }
    float s = c / (-b + sqrt(max(disc, 0.0)));
    vec3 a = h * u_target + s * d;
    float chord = length(a);
    float sine = k * chord / 2.0;
    float rho = chord * (sine < 1e-4 ? 1.0 + sine * sine / 6.0 : asin(min(1.0, sine)) / sine);
    if (rho > PI) { outColor = vec4(0.0); return; }
    vec3 along = a - u_target * dot(a, u_target);
    float alongLength = length(along);
    world = alongLength < 1e-30 ? u_target : u_target * cos(rho) + along * (sin(rho) / alongLength);
    // The globe darkens toward its limb, less as it unfurls.
    shade = mix(1.0, 0.78 + 0.22 * max(0.0, dot(normalize(u_target + k * a), -d)), k);
  }
  float pixelRadians = max(length(dFdx(world)), length(dFdy(world)));
  g_metersPerPixel = pixelRadians * 6371008.8;
  vec3 direction = u_toCube * world;
  ${levels}
  ${categories}
  vec3 paint = toLinear(vec4(u_background, 1.0)).rgb;
  ${steps}
  vec3 color = toSrgb(vec4(paint * shade, 1.0)).rgb;
  if (u_overlays > 0) {
    float lon = atan(world.y, world.x);
    float lat = asin(clamp(world.z, -1.0, 1.0));
    for (int index = u_overlays - 1; index >= 0; index--) {
      vec4 bounds = u_overlayBounds[index];
      float u = mod(lon - bounds.x, 2.0 * PI) / (bounds.z - bounds.x);
      float mercator = log(tan(PI / 4.0 + clamp(lat, -1.4844, 1.4844) / 2.0));
      float top = u_overlayMercator[index] > 0.5 ? log(tan(PI / 4.0 + bounds.w / 2.0)) : bounds.w;
      float bottom = u_overlayMercator[index] > 0.5 ? log(tan(PI / 4.0 + bounds.y / 2.0)) : bounds.y;
      float v = ((u_overlayMercator[index] > 0.5 ? mercator : lat) - top) / (bottom - top);
      // Read everywhere and kept only inside, so every pixel takes the same path to the texture.
      float inside = step(0.0, u) * step(u, 1.0) * step(0.0, v) * step(v, 1.0);
      vec2 at = clamp(vec2(u, v), 0.0, 1.0);
      vec4 picture = index == 0 ? texture(u_overlay0, at)
        : index == 1 ? texture(u_overlay1, at)
        : index == 2 ? texture(u_overlay2, at)
        : texture(u_overlay3, at);
      color = mix(color, picture.rgb, inside * picture.a * u_overlayOpacity[index]);
    }
  }

  if (u_grid) {
    vec3 f = faceSt(direction);
    vec2 p = vec2(f.x, 1.0 - f.y) * exp2(u_gridLevel);
    vec2 width = fwidth(p);
    float spacing = 1.0 / max(width.x, width.y);
    if (spacing > 6.0) {
      vec2 line = abs(fract(p - 0.5) - 0.5) / width;
      float on = 1.0 - min(min(line.x, line.y), 1.0);
      color = mix(color, u_dark ? vec3(1.0) : vec3(0.0), on * 0.18 * clamp((spacing - 6.0) / 10.0, 0.0, 1.0));
    }
  }
  if (u_brushRadians > 0.0) {
    // Screen pixels to the nearest side of this texel that parts a painted texel from one that isn't.
    vec3 f = faceSt(direction);
    vec2 p = vec2(f.x, 1.0 - f.y) * u_brushSize;
    vec2 texel = floor(p);
    vec2 inside = fract(p);
    vec2 width = max(fwidth(p), vec2(1e-6));
    bool painted = brushed(f.z, texel);
    float ring = 1e6;
    if (brushed(f.z, texel + vec2(-1.0, 0.0)) != painted) ring = min(ring, inside.x / width.x);
    if (brushed(f.z, texel + vec2(1.0, 0.0)) != painted) ring = min(ring, (1.0 - inside.x) / width.x);
    if (brushed(f.z, texel + vec2(0.0, -1.0)) != painted) ring = min(ring, inside.y / width.y);
    if (brushed(f.z, texel + vec2(0.0, 1.0)) != painted) ring = min(ring, (1.0 - inside.y) / width.y);
    color = mix(color, vec3(0.0), clamp(2.6 - ring, 0.0, 1.0) * 0.45);
    color = mix(color, vec3(1.0), clamp(1.4 - ring, 0.0, 1.0));
  }
  outColor = vec4(color * edge, edge);
}`;
}

interface Program {
  program: WebGLProgram | null;
  uniforms: Map<string, WebGLUniformLocation | null>;
  /** Why it didn't build, if it didn't. */
  problem: string | null;
  /** Its fragment shader while it compiles in the background, for the log if it fails. */
  compiling: WebGLShader | null;
}

/** A raster's pages, and which tile is on each. */
interface Pages {
  texture: WebGLTexture;
  type: RasterType;
  /** The page each tile is on, by place key. */
  tiles: Map<string, { place: TilePlace; page: number; kept: number }>;
  free: number[];
}

/**
 * `pages` is how many tiles each raster holds on the GPU at once: enough for a screen at one level
 * and the coarser ones behind it while finer ones load.
 *
 * `texelTarget` defaults to the screen's device pixels per point, so a map pixel covers about a
 * point: a phone with a 3× screen loads about a ninth of what it would at one device pixel.
 *
 * With `onReady`, a paint order's shader compiles in the background where the browser can: until
 * it's done, drawing leaves the canvas as it was, and `onReady` says when to draw again. Without
 * it, the first draw waits for the shader, so the canvas can be read straight after.
 */
export function createRenderer(
  canvas: HTMLCanvasElement,
  {
    pages: capacity = 96,
    onReady,
    texelTarget = Math.max(1, window.devicePixelRatio || 1),
  }: { pages?: number; onReady?: () => void; texelTarget?: number } = {},
): Renderer {
  const gl = canvas.getContext("webgl2", {
    antialias: false,
    premultipliedAlpha: true,
    alpha: true,
  });
  if (!gl) throw new Error("This browser can't draw maps: it has no WebGL 2.");
  const parallel = onReady ? gl.getExtension("KHR_parallel_shader_compile") : null;
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);

  const rasters = new Map<string, Pages>();
  const table = new Uint32Array(TABLE_SLOTS * 4);
  let tableChanged = true;
  let clock = 0;
  const tableTexture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tableTexture);
  gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA32UI, TABLE_WIDTH, TABLE_SLOTS / TABLE_WIDTH);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  let world: RendererWorld | null = null;
  let worldShape = "";
  /** Each paint order's program for this world, built as it's first drawn. */
  const programs = new Map<string, Program>();
  let rods: ReturnType<typeof createAxis> | null = null;
  /** Each overlay's picture, as a texture, by its id. */
  const pictures = new Map<string, WebGLTexture>();
  // A sampler always has something to read, even with no overlay on it.
  const emptyPicture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, emptyPicture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));

  const formats = (type: RasterType) =>
    type === "color"
      ? { internal: gl.RGBA8, format: gl.RGBA, kind: gl.UNSIGNED_BYTE }
      : type === "category"
        ? { internal: gl.R8UI, format: gl.RED_INTEGER, kind: gl.UNSIGNED_BYTE }
        : { internal: gl.R32I, format: gl.RED_INTEGER, kind: gl.INT };

  const pagesFor = (name: string, type: RasterType) => {
    const existing = rasters.get(name);
    if (existing?.type === type) return;
    if (existing) gl.deleteTexture(existing.texture);
    const texture = gl.createTexture();
    const format = formats(type);
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.texStorage3D(
      gl.TEXTURE_2D_ARRAY,
      1,
      format.internal,
      BASEMAP_TILE_PIXELS,
      BASEMAP_TILE_PIXELS,
      capacity,
    );
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    rasters.set(name, {
      texture,
      type,
      tiles: new Map(),
      free: Array.from({ length: capacity }, (_, page) => capacity - 1 - page),
    });
    tableChanged = true;
  };

  /** Writes every loaded tile into the page table, keyed by the raster's place in this world. */
  const writeTable = () => {
    table.fill(0);
    world?.rasters.forEach((raster, index) => {
      for (const { place, page } of rasters.get(raster.name)?.tiles.values() ?? []) {
        const head = index * 512 + place.level * 8 + place.face;
        const start = slotOf(head, place.x, place.y);
        for (let probe = 0; probe < TABLE_PROBES; probe++) {
          const slot = ((start + probe) & (TABLE_SLOTS - 1)) * 4;
          if (table[slot + 3] !== 0) continue;
          table.set([head, place.x, place.y, page + 1], slot);
          break;
        }
      }
    });
    gl.bindTexture(gl.TEXTURE_2D, tableTexture);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      TABLE_WIDTH,
      TABLE_SLOTS / TABLE_WIDTH,
      gl.RGBA_INTEGER,
      gl.UNSIGNED_INT,
      table,
    );
    tableChanged = false;
  };

  const forget = () => {
    for (const built of programs.values()) if (built.program) gl.deleteProgram(built.program);
    programs.clear();
  };

  /** The program for a paint order, built the first time it's asked for. */
  const programFor = (paint: Paint, hidden: ReadonlySet<string>): Program => {
    const key = JSON.stringify([paint.paintOrder, Object.keys(paint.variables), [...hidden]]);
    let built = programs.get(key);
    if (!built) {
      built = build(paint, hidden);
      programs.set(key, built);
      // A handful are kept: the views in use, and a few just left.
      const oldest = programs.keys().next().value;
      if (programs.size > 8 && oldest !== undefined) {
        const dropped = programs.get(oldest)?.program;
        if (dropped) gl.deleteProgram(dropped);
        programs.delete(oldest);
      }
    }
    return built;
  };

  const build = (paint: Paint, hidden: ReadonlySet<string>): Program => {
    const failed = (problem: string) => ({
      program: null,
      uniforms: new Map(),
      problem,
      compiling: null,
    });
    if (!world) return failed("There's no world to draw.");
    let source: string;
    try {
      source = fragmentFor(world, paint, hidden);
    } catch (error) {
      return failed(error instanceof Error ? error.message : String(error));
    }
    const vertex = shader(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = shader(gl, gl.FRAGMENT_SHADER, source);
    const next = gl.createProgram();
    gl.attachShader(next, vertex);
    gl.attachShader(next, fragment);
    gl.bindAttribLocation(next, 0, "a_position");
    gl.linkProgram(next);
    const built = { program: next, uniforms: new Map(), problem: null, compiling: fragment };
    if (!parallel) settle(built);
    return built;
  };

  /** Finishes a program's build, which waits for its compile if it's still going. */
  const settle = (built: Program) => {
    const { program, compiling: fragment } = built;
    built.compiling = null;
    if (!program || gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    built.problem =
      gl.getProgramInfoLog(program) ||
      (fragment && gl.getShaderInfoLog(fragment)) ||
      "The map's shader didn't build.";
    gl.deleteProgram(program);
    built.program = null;
  };

  let waiting = 0;
  /** Checks each frame for a background compile to finish, then says to draw again. */
  const waitFor = (built: Program) => {
    if (waiting || !parallel || !built.program) return;
    const program = built.program;
    const check = () => {
      if (gl.isContextLost()) {
        waiting = 0;
        return;
      }
      // Dropped for newer paint orders: the next draw watches whichever program it needs.
      if (!gl.isProgram(program)) {
        waiting = 0;
        onReady?.();
        return;
      }
      if (!gl.getProgramParameter(program, parallel.COMPLETION_STATUS_KHR)) {
        waiting = requestAnimationFrame(check);
        return;
      }
      waiting = 0;
      onReady?.();
    };
    waiting = requestAnimationFrame(check);
  };

  /** The program for a paint order, or null while its shader compiles in the background. */
  const readyProgram = (paint: Paint, hidden: ReadonlySet<string>): Program | null => {
    const built = programFor(paint, hidden);
    if (!built.compiling) return built;
    if (
      parallel &&
      built.program &&
      !gl.getProgramParameter(built.program, parallel.COMPLETION_STATUS_KHR)
    ) {
      waitFor(built);
      return null;
    }
    settle(built);
    return built;
  };

  /** Draws into the drawing buffer's bottom-left `width` × `height`, at `ratio` pixels a point. */
  const render = (
    {
      camera,
      size,
      paint,
      dark,
      values,
      grid,
      hidden,
      brush,
      axis = 0,
      overlays = [],
    }: DrawOptions,
    width: number,
    height: number,
    ratio: number,
  ): string | null => {
    const built = world ? readyProgram(paint, hidden) : null;
    // Its shader is still compiling, so the canvas keeps the last frame drawn.
    if (world && !built) return null;
    gl.viewport(0, 0, width, height);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(0, 0, width, height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.SCISSOR_TEST);
    if (!world || !built) return null;
    const program = built.program;
    if (!program) return built.problem;
    const uniform = (name: string) => {
      if (!built.uniforms.has(name)) built.uniforms.set(name, gl.getUniformLocation(program, name));
      return built.uniforms.get(name) ?? null;
    };
    // biome-ignore lint/correctness/useHookAtTopLevel: WebGL's useProgram, not a React hook.
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2f(uniform("u_resolution"), width, height);
    gl.uniform1i(uniform("u_view"), camera.kind === "flat" ? 0 : 1);
    if (camera.kind === "flat") {
      gl.uniform1f(uniform("u_scale"), camera.scale * ratio);
      gl.uniform2f(uniform("u_flatCenter"), camera.x, camera.y);
    } else {
      const globe = camera.camera;
      gl.uniform3fv(uniform("u_target"), globe.target);
      gl.uniform3fv(uniform("u_east"), globe.east);
      gl.uniform3fv(uniform("u_north"), globe.north);
      gl.uniform1f(uniform("u_altitude"), globe.altitude);
      gl.uniform1f(uniform("u_curvature"), globe.curvature);
      gl.uniform1f(uniform("u_focal"), globe.focal * ratio);
    }
    // GLSL's mat3 is column by column; the orientation is row by row.
    gl.uniformMatrix3fv(uniform("u_toCube"), true, world.orientation.flat());
    gl.uniform1i(uniform("u_dark"), dark ? 1 : 0);
    gl.uniform3fv(uniform("u_background"), dark ? [0.13, 0.13, 0.15] : [0.93, 0.92, 0.9]);
    const finest = Math.max(
      0,
      ...world.rasters.filter(raster => !raster.feature).map(raster => raster.deepest),
    );
    gl.uniform1i(uniform("u_grid"), grid ? 1 : 0);
    gl.uniform1f(uniform("u_gridLevel"), finest);
    gl.uniform3fv(
      uniform("u_brush"),
      brush ? toCube(world.orientation, brush.direction) : [0, 0, 1],
    );
    gl.uniform1f(uniform("u_brushRadians"), brush ? brush.radians : 0);
    gl.uniform1f(uniform("u_brushSize"), brush ? brush.size : 1);
    gl.uniform1f(uniform("u_texelTarget"), texelTarget);
    for (const [name, variable] of Object.entries(paint.variables)) {
      const value = values[name] ?? variable.default;
      if (typeof value === "boolean") gl.uniform1i(uniform(variableUniform(name)), value ? 1 : 0);
      else gl.uniform1f(uniform(variableUniform(name)), Number(value));
    }
    if (tableChanged) writeTable();
    world.rasters.forEach((raster, index) => {
      const pages = rasters.get(raster.name);
      if (!pages) return;
      gl.activeTexture(gl.TEXTURE0 + index);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, pages.texture);
      gl.uniform1i(uniform(`u_raster${index}`), index);
      gl.uniform1f(uniform(`u_deepest${index}`), raster.deepest);
    });
    gl.activeTexture(gl.TEXTURE0 + world.rasters.length);
    gl.bindTexture(gl.TEXTURE_2D, tableTexture);
    gl.uniform1i(uniform("u_pages"), world.rasters.length);
    const drawn = overlays.filter(overlay => pictures.has(overlay.id)).slice(0, MOST_OVERLAYS);
    const radians = (degrees: number) => (degrees * Math.PI) / 180;
    for (let index = 0; index < MOST_OVERLAYS; index++) {
      const overlay = drawn[index];
      const unit = world.rasters.length + 1 + index;
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, overlay ? (pictures.get(overlay.id) ?? null) : emptyPicture);
      gl.uniform1i(uniform(`u_overlay${index}`), unit);
    }
    gl.uniform1i(uniform("u_overlays"), drawn.length);
    gl.uniform4fv(
      uniform("u_overlayBounds"),
      drawn
        .flatMap(({ bounds }) => {
          const east = bounds.east > bounds.west ? bounds.east : bounds.east + 360;
          return [
            radians(bounds.west),
            radians(bounds.south),
            radians(east),
            radians(bounds.north),
          ];
        })
        .concat(Array(4 * (MOST_OVERLAYS - drawn.length)).fill(0)),
    );
    gl.uniform1fv(
      uniform("u_overlayMercator"),
      Array.from({ length: MOST_OVERLAYS }, (_, index) =>
        drawn[index]?.projection === "webMercator" ? 1 : 0,
      ),
    );
    gl.uniform1fv(
      uniform("u_overlayOpacity"),
      Array.from({ length: MOST_OVERLAYS }, (_, index) => drawn[index]?.opacity ?? 0),
    );
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (camera.kind === "globe" && axis > 0.01) {
      rods ??= createAxis(gl);
      rods.draw(camera.camera, axis, size);
    }
    return null;
  };

  return {
    texelTarget,
    setWorld(next) {
      // A world whose rasters only go deeper or shallower keeps its programs: depth is a uniform.
      const shape = JSON.stringify({
        ...next,
        rasters: next.rasters.map(({ deepest: _, ...raster }) => raster),
      });
      const reshaped = shape !== worldShape;
      worldShape = shape;
      world = next;
      const kept = new Set(next.rasters.map(raster => raster.name));
      for (const [name, pages] of rasters) {
        if (!kept.has(name)) {
          gl.deleteTexture(pages.texture);
          rasters.delete(name);
        }
      }
      for (const raster of next.rasters) pagesFor(raster.name, raster.type);
      tableChanged = true;
      if (reshaped) forget();
    },
    setTile(name, place, pixels, rect) {
      const pages = rasters.get(name);
      if (!pages) return false;
      const key = placeKey(place);
      let held = pages.tiles.get(key);
      if (!held) {
        let page = pages.free.pop();
        if (page === undefined) {
          // The page kept longest ago goes, but never a face's whole tile, which everything falls
          // back to.
          let oldest: [string, { page: number; kept: number }] | null = null;
          for (const entry of pages.tiles) {
            if (entry[1].place.level === BASEMAP_COARSEST_LEVEL) continue;
            if (!oldest || entry[1].kept < oldest[1].kept) oldest = entry;
          }
          if (!oldest) return false;
          pages.tiles.delete(oldest[0]);
          page = oldest[1].page;
        }
        held = { place, page, kept: ++clock };
        pages.tiles.set(key, held);
        tableChanged = true;
        rect = undefined;
      }
      const area = rect ?? { x: 0, y: 0, width: BASEMAP_TILE_PIXELS, height: BASEMAP_TILE_PIXELS };
      const format = formats(pages.type);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, pages.texture);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, BASEMAP_TILE_PIXELS);
      gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, area.x);
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, area.y);
      gl.texSubImage3D(
        gl.TEXTURE_2D_ARRAY,
        0,
        area.x,
        area.y,
        held.page,
        area.width,
        area.height,
        1,
        format.format,
        format.kind,
        pixels,
      );
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
      gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
      return true;
    },
    setOverlayImage(id, image) {
      const held = pictures.get(id);
      if (held) gl.deleteTexture(held);
      pictures.delete(id);
      if (!image) return;
      const texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      pictures.set(id, texture);
    },
    hasTile(name, place) {
      return rasters.get(name)?.tiles.has(placeKey(place)) ?? false;
    },
    dropTiles(name, under) {
      const pages = rasters.get(name);
      if (!pages) return;
      for (const [key, held] of pages.tiles) {
        const { place } = held;
        const shift = place.level - under.level;
        if (
          place.face === under.face &&
          shift >= 0 &&
          Math.floor(place.x / 2 ** shift) === under.x &&
          Math.floor(place.y / 2 ** shift) === under.y
        ) {
          pages.tiles.delete(key);
          pages.free.push(held.page);
          tableChanged = true;
        }
      }
    },
    keep(name, places) {
      const pages = rasters.get(name);
      if (!pages) return;
      clock++;
      for (const place of places) {
        const held = pages.tiles.get(placeKey(place));
        if (held) held.kept = clock;
      }
    },
    draw(options) {
      const ratio = window.devicePixelRatio || 1;
      const width = Math.round(options.size.width * ratio);
      const height = Math.round(options.size.height * ratio);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return render(options, width, height, ratio);
    },
    drawInto(target, options) {
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.round(options.size.width * ratio);
      const height = Math.round(options.size.height * ratio);
      if (!width || !height || width > canvas.width || height > canvas.height) return false;
      if (world && !readyProgram(options.paint, options.hidden)) return false;
      if (target.width !== width || target.height !== height) {
        target.width = width;
        target.height = height;
      }
      render(options, width, height, ratio);
      const context = target.getContext("2d");
      context?.clearRect(0, 0, width, height);
      context?.drawImage(canvas, 0, canvas.height - height, width, height, 0, 0, width, height);
      return true;
    },
    dispose() {
      cancelAnimationFrame(waiting);
      rods?.dispose();
      for (const pages of rasters.values()) gl.deleteTexture(pages.texture);
      for (const texture of pictures.values()) gl.deleteTexture(texture);
      gl.deleteTexture(emptyPicture);
      gl.deleteTexture(tableTexture);
      forget();
      gl.deleteBuffer(buffer);
    },
  };
}

function toCube(orientation: readonly (readonly number[])[], [x, y, z]: Vector3): Vector3 {
  const row = (index: number) => orientation[index] ?? [0, 0, 0];
  const dot = (r: readonly number[]) => (r[0] ?? 0) * x + (r[1] ?? 0) * y + (r[2] ?? 0) * z;
  return [dot(row(0)), dot(row(1)), dot(row(2))];
}

function shader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const made = gl.createShader(type);
  if (!made) throw new Error("The map's shader couldn't be made.");
  gl.shaderSource(made, source);
  gl.compileShader(made);
  return made;
}
